<template>
  <div class="fleet-utilization-chart rounded-lg border border-gray-200 p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('FLEET_UTILIZATION') }}
        </h3>
        <p class="text-sm text-gray-500">
          {{ $t('FLEET_UTILIZATION_AVERAGE_OVER_TIME') }}
        </p>
      </div>
      <!-- <el-button
        type="text"
        class="text-blue-600 hover:text-blue-800 focus-within:outline-none"
        @click="showAllSeries"
      >
        {{ $t('VIEW_ALL') }}
      </el-button> -->
    </div>

    <!-- Chart -->
    <div class="chart-container">
      <apexchart
        ref="chart"
        v-if="chartOptions && chartSeries.length"
        type="bar"
        height="240"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div v-else class="flex items-center justify-center h-64 text-gray-500">
        {{ $t('NO_DATA') }}
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'FleetUtilizationChart',
  components: { apexchart: VueApexCharts },
  props: {
    // expects: [ {bucket, fleet_utilization_average, start_bucket_date, end_bucket_date}, ... ]
    data: { type: Object, default: () => ({ results: [] }) },
  },
  computed: {
    labels() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return []
      return this.data.results.map(function (r) {
        // Format dates to be more compact and readable
        const startDate = new Date(r.start_bucket_date)
        const endDate = new Date(r.end_bucket_date)

        // Use shorter format: MM/DD
        const startFormatted = startDate.toLocaleDateString('fa-IR', {
          month: '2-digit',
          day: '2-digit',
        })
        const endFormatted = endDate.toLocaleDateString('fa-IR', {
          month: '2-digit',
          day: '2-digit',
        })

        return startFormatted + ' - ' + endFormatted
      })
    },
    chartSeries() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return []
      return [
        {
          name: this.$t('FLEET_UTILIZATION_AVERAGE'),
          data: this.data.results.map(function (r) {
            return Number(r.fleet_utilization_average || 0)
          }),
        },
        {
          name: this.$t('FLEET_DOWNTIME_AVERAGE') || 'Fleet Downtime',
          data: this.data.results.map(function (r) {
            return Number(r.fleet_downtime_average || 0)
          }),
        },
      ]
    },
    chartOptions() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return null

      var self = this
      return {
        chart: {
          type: 'bar',
          height: 200,
          stacked: true,
          toolbar: { show: false },
          animations: { enabled: true, easing: 'easeinout', speed: 500 },
          zoom: { enabled: false },
          events: {
            legendClick: function (chartContext, seriesIndex, config) {
              // Show all series when legend is clicked
              var seriesArr =
                config && config.w && config.w.config && config.w.config.series
                  ? config.w.config.series
                  : config && config.config && config.config.series
                  ? config.config.series
                  : []
              seriesArr.forEach(function (s, i) {
                chartContext.showSeries(s.name)
              })
              return false
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded',
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: self.labels,
          labels: {
            // rotate: -45,
            // rotateAlways: true,
            // hideOverlappingLabels: true,
            maxHeight: 60,
            style: {
              fontSize: '11px',
              colors: '#6B7280',
              fontWeight: 400,
            },
            offsetY: 5,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          title: {
            text: this.$t('HOURS') || 'Hours',
            style: { fontSize: '12px', color: '#6B7280' },
            offsetX: -10,
          },
          labels: {
            style: { fontSize: '12px', colors: '#6B7280' },
            formatter: function (val) {
              return Number(val).toFixed(1) + 'h'
            },
            offsetX: -15,
          },
          max: 24,
        },
        // fill: {
        //   type: 'gradient',
        //   gradient: {
        //     shadeIntensity: 1,
        //     opacityFrom: 0.3,
        //     opacityTo: 0.1,
        //     stops: [0, 90, 100],
        //   },
        // },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (val, opts) {
              return Number(val).toFixed(1) + 'h'
            },
          },
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const data = self.data.results[dataPointIndex]
            const utilizationHours = Number(
              data.fleet_utilization_average || 0
            ).toFixed(1)
            const downtimeHours = Number(
              data.fleet_downtime_average || 0
            ).toFixed(1)
            const utilizationPercentage = Number(
              data.fleet_utilization_average_percentage || 0
            ).toFixed(1)
            const dateRange = self.labels[dataPointIndex]

            return `
              <div class="apexcharts-tooltip-custom" style="padding: 12px; background: white; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                <div style="font-weight: 600; margin-bottom: 8px; color: #374151;">${dateRange}</div>
                <div style="display: flex; align-items: center; margin-bottom: 4px;">
                  <div style="width: 12px; height: 12px; background-color: #10B981; border-radius: 2px; margin-left: 8px;"></div>
                  <span style="margin-left: 8px; color: #374151;">${
                    self.$t('FLEET_UTILIZATION_AVERAGE') || 'Fleet Utilization'
                  }: ${utilizationHours} ${self.$t(
              'HOURS'
            )} (${utilizationPercentage}%)</span>
                </div>
                <div style="display: flex; align-items: center;">
                  <div style="width: 12px; height: 12px; background-color: #6B7280; border-radius: 2px; margin-left: 8px;"></div>
                  <span style="margin-left: 8px; color: #374151;">${
                    self.$t('FLEET_DOWNTIME_AVERAGE') || 'Fleet Downtime'
                  }: ${downtimeHours} ${self.$t('HOURS')}</span>
                </div>
              </div>
            `
          },
        },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'center',
          floating: false,
          offsetY: 10,
          onItemClick: {
            toggleDataSeries: false,
          },
          onItemHover: {
            highlightDataSeries: true,
          },
        },
        grid: {
          borderColor: '#F3F4F6',
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        colors: ['#10B981', '#6B7280'],
      }
    },
  },
  methods: {
    showAllSeries() {
      if (!this.$refs.chart || !this.chartSeries || !this.chartSeries.length)
        return
      var names = this.chartSeries.map(function (s) {
        return s.name
      })
      var chart = this.$refs.chart.chart
      names.forEach(function (n) {
        chart.showSeries(n)
      })
    },
  },
}
</script>

<style scoped>
.fleet-utilization-chart {
  min-height: 300px;
}
.chart-container {
  width: 100%;
}
.chart-container >>> .apexcharts-xaxis-label,
.chart-container >>> .apexcharts-yaxis-label {
  font-family: inherit;
}
</style>
